import { useMemo } from 'react';
import type { ReaderPosition, Settings } from '@jerome2/shared';
import { createStore, useStore } from '../lib/store.ts';
import { app, saveSettings } from '../state/app.ts';
import { errorToast } from '../state/toast.ts';
import type { MenuEntry, MenuItem } from './ContextMenu.tsx';

// De indeling van lijst en lezer op het tabblad Mail (desktop; ticket 28625, wens van de eigenaar: het leesvenster makkelijker
// groter maken, en in deel 2 verplaatsen). De breedte van de lijst stelt de gebruiker in met de greep tussen lijst en lezer
// (`settings.layout.mailList`; de lezer houdt 480 px van de rij, state/layout.ts). Met één klik klapt de lijst in tot een strook
// van 44 px en krijgt de lezer de rest (`settings.mail.listVisible`: de knop Lezer vergroten in de werkbalk van de lezer, het menu
// Weergave in de filterbalk en de knop op de strook). Zonder open mail staat de lijst er altijd, zonder dat de keuze verandert.
// Deel 2: het leesvenster staat rechts van de lijst of eronder, zoals in Outlook (`settings.mail.readerPosition`, het menu
// Weergave); onder is de hoogte van de lijst de instelling `layout.mailSplit` (MailLayout.tsx). Op de telefoon verandert niets.

/** De keuzes voor de indeling van Mail: de lijst zichtbaar, en de plek van het leesvenster. */
export type MailChoices = Settings['mail'];

/** Wat nog onderweg is naar de server, voor alle mailschermen samen (ook met Mail in twee panelen), en het volgnummer van de
 * laatste wijziging. Elke wijziging stuurt de hele `mail` met wat al onderweg was erbij: zo zetten twee keuzes kort na elkaar
 * (de lijst verbergen en het leesvenster onder zetten) elkaar niet terug. */
const pending = createStore<Partial<MailChoices>>({});
let latest = 0;

/** Een keuze bewaren: meteen in beeld, daarna met de hele `mail` naar de server. Mislukt de laatste wijziging, dan komt de bewaarde
 * stand terug met de melding "Instelling niet bewaard"; een eerdere fout telt niet als de volgende wijziging hem meestuurde. */
export function setMailChoices(patch: Partial<MailChoices>): void {
  const n = ++latest;
  const next = { ...pending.get(), ...patch };
  pending.set(next);
  void saveSettings({ mail: { ...app.get().settings.mail, ...next } })
    .catch((e: unknown) => {
      if (latest === n) errorToast(e, 'Instelling niet bewaard');
    })
    .finally(() => {
      if (latest === n) pending.set({});
    });
}

/** De keuzes van nu: wat onderweg is, anders het bewaarde. */
export function useMailChoices(): MailChoices {
  const stored = useStore(app, (s) => s.settings.mail);
  const own = useStore(pending);
  return useMemo(() => ({ ...stored, ...own }), [stored, own]);
}

/** De regels van het menu Weergave in de filterbalk (desktop): de lijst verbergen of tonen (zonder open mail is de lijst er altijd,
 * dus dan uitgeschakeld), en na een scheiding de plek van het leesvenster als keuze met een vinkje (ook zonder open mail: de plek
 * geldt voor de volgende mail). Er kunnen regels bij zonder dat de filterbalk verandert. */
export function viewMenuItems(state: { listVisible: boolean; mailOpen: boolean; readerPosition: ReaderPosition }, on: { setListVisible: (visible: boolean) => void; setReaderPosition: (position: ReaderPosition) => void }): MenuEntry[] {
  const place = (position: ReaderPosition, id: string, label: string): MenuItem => ({
    id,
    label,
    checked: state.readerPosition === position,
    onSelect: () => {
      if (state.readerPosition !== position) on.setReaderPosition(position);
    },
  });
  return [
    { id: 'list', label: state.listVisible ? 'Lijst verbergen' : 'Lijst tonen', icon: state.listVisible ? 'expand' : 'shrink', disabled: !state.mailOpen, onSelect: () => on.setListVisible(!state.listVisible) },
    'separator',
    place('right', 'readerRight', 'Leesvenster rechts'),
    place('below', 'readerBelow', 'Leesvenster onder'),
  ];
}
